import type { AnyBuild, PhaseView } from './buildsApi';
import { isOwnerView } from './buildsApi';

/**
 * The words of the Builds list and a build's page (#1624, W21): every sentence is a template keyed by a state, a step
 * code, a count name or a reason code. A count with no template is not shown; a reason with no words reads
 * "Stopped (code)". The only text shown that the UI did not write is the owner's build name and the owner's own
 * display name.
 */

/** The build's model spend cap (`BUILD_USD`, the builder session's ledger): "$x of $40". */
export const MODEL_CAP_USD = 40;

export type Tone = 'run' | 'ok' | 'bad' | 'muted';

/** The status chip (R11). `wide`: the viewer sees the workspace's queue (an admin, a personal workspace). */
export function statusOf(b: AnyBuild): { label: string; tone: Tone } {
  switch (b.state) {
    case 'running': return isOwnerView(b) && b.stopping ? { label: 'Stopping', tone: 'muted' } : { label: 'Running', tone: 'run' };
    case 'queued': return typeof b.queue_position === 'number'
      ? { label: `Queued, ${b.queue_position} in line`, tone: 'muted' } : { label: 'Waiting for a slot', tone: 'muted' };
    case 'ready': return { label: 'Ready to publish', tone: 'ok' };
    case 'published': return { label: 'Published', tone: 'ok' };
    case 'stopped': return { label: 'Stopped', tone: 'muted' };
    case 'failed': return { label: 'Failed', tone: 'bad' };
    default: return { label: 'Unknown', tone: 'muted' };
  }
}

export const STEP_LABELS: Record<string, string> = {
  profile: 'Read your tables',
  context: 'Read your documents',
  plan: 'Draft the plan',
  merge: 'Merge the plans',
  check: 'Test the plan on your data',
  build: 'Build the model',
  qa: 'Ask it questions and check the answers',
};

const n = (v: number) => v.toLocaleString('en-US');
const plural = (v: number, one: string, many = one + 's') => `${n(v)} ${v === 1 ? one : many}`;

/** Count templates by step (the names the session reports: `__fixtures__/sessionCounts.json`). `null`: not shown. */
export const COUNT_WORDS: Record<string, Record<string, ((v: number) => string | null)>> = {
  profile: {
    tables: (v) => plural(v, 'table'),
    columns: (v) => plural(v, 'column'),
    statements: (v) => plural(v, 'read-only query', 'read-only queries'),
    incomplete: (v) => (v ? `${plural(v, 'table')} read in part` : null),
    // #1999 S4: a Snowflake column whose name the model cannot hold (lower or mixed case, a reserved word, symbols)
    unplannable_columns: (v) => (v ? `${plural(v, 'column')} left out: their names can’t be used in the model` : null),
    warehouse_bytes: () => null,                       // shown once, as "Warehouse read"
    warehouse_seconds: () => null,                     // shown once, as "Warehouse time"
  },
  context: {
    catalog_hints: (v) => `${plural(v, 'note')} from the warehouse catalog`,
    catalog_not_read: () => 'the warehouse catalog couldn’t be read',
    documents_read: (v) => `${plural(v, 'document')} read`,
    documents_restricted: (v) => (v ? `${plural(v, 'document')} not read in an organization workspace` : null),
    documents_missing: (v) => (v ? `${plural(v, 'document')} no longer there` : null),
    documents_refused: (v) => (v ? `${plural(v, 'document')} the build couldn’t read` : null),
    repositories_read: (v) => `${plural(v, 'repository', 'repositories')} read`,
    repositories_not_read: (v) => (v ? `${plural(v, 'repository', 'repositories')} the build couldn’t read` : null),
    hints: (v) => `${plural(v, 'hint')} kept`,
    hints_cut: (v) => (v ? `${plural(v, 'hint')} over the limit left out` : null),
    pack_refused: () => 'the context couldn’t be used',
    // query history (#1479): shapes only, never a query's text
    history_off: () => 'query history not read (turned off)',
    history_not_read: () => 'the query history couldn’t be read',
    history_unsupported: () => null,
    history_unreadable: () => null,
    history_budget: () => null,
    history_deadline: () => null,
    history_bad_location: () => null,
    history_rows: (v) => `${plural(v, 'query history entry', 'query history entries')} read`,
    history_admitted: () => null,
    history_unparsed: () => null,
    history_not_read_only: () => null,
    history_too_long: (v) => (v ? `${plural(v, 'query history entry', 'query history entries')} too long to read` : null),
    history_cap_hit: (v) => (v ? 'query history was limited' : null),
    history_hints: (v) => `${plural(v, 'usage hint')} from query history`,
    // dashboards (#1481)
    dashboards_restricted: (v) => (v ? `${plural(v, 'dashboard connection')} not read in an organization workspace` : null),
    dashboards_missing: (v) => (v ? `${plural(v, 'dashboard connection')} not found` : null),
    dashboards_unreadable: (v) => (v ? `${plural(v, 'dashboard connection')} the build couldn’t read` : null),
    dashboards_refused: (v) => (v ? `${plural(v, 'dashboard connection')} the build refused to read` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  plan: {
    items: (v) => `${plural(v, 'item')} planned`,
    refused: (v) => (v ? `${plural(v, 'item')} set aside` : null),
    planners_run: (v) => `${plural(v, 'plan')} written`,
    planners_failed: (v) => (v ? `${plural(v, 'plan')} that couldn’t be read` : null),
    planners_cut: (v) => (v ? `${plural(v, 'plan')} not written: its budget ran out` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  merge: {
    merged_items: (v) => `${plural(v, 'item')} in the merged plan`,
    variants: () => null,
    decisions: (v) => (v ? `${plural(v, 'reading')} for you to decide` : null),
    citations_verified: (v) => (v ? `${plural(v, 'default')} your documents state` : null),
    adjudicated: () => null,
    notes: () => null,
    gaps: (v) => (v ? `${plural(v, 'item')} set aside` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  check: {
    passed: (v) => `${n(v)} passed`,
    failed: (v) => (v ? `${n(v)} failed` : null),
    insufficient: (v) => (v ? `${n(v)} couldn’t be settled` : null),
    not_testable: (v) => (v ? `${n(v)} not testable` : null),
    measured: (v) => (v ? `${n(v)} measured for the record` : null),   // fan-out, coverage: a fact, not a verdict
    gaps: (v) => (v ? `${plural(v, 'gap')} left out of the model` : null),
    repair_rounds: () => null,                         // shown as the phase's rounds
    questions: (v) => (v ? `${plural(v, 'question')} for you` : null),
    hint_notes: (v) => (v ? `${plural(v, 'hint')} the data didn’t bear out` : null),
    hint_questions: (v) => (v ? `${plural(v, 'reading')} from your documents to decide` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  build: {
    models: (v) => plural(v, 'model'),
    compiled: (v) => `${plural(v, 'query', 'queries')} compiled`,
    rounds: () => null,
    unbound: (v) => (v ? `${plural(v, 'draft')} sent back for not matching the plan` : null),
    missing_readings: (v) => (v ? `${plural(v, 'chosen reading')} not built yet` : null),
    perspective_shape: (v) => (v ? `${plural(v, 'perspective gap')} left for your review` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  qa: {
    rounds: () => null,
    round: () => null,
    questions: () => null,
    first_try_rate: (v) => `${v}% right first time`,
    first_try_pct: () => null,
    final_pct: () => null,
    passed: (v) => `${plural(v, 'answer')} checked`,
    gaps: (v) => (v ? `${plural(v, 'gap')} still open` : null),
    fixed: () => null,
    regressions: () => null,
    fixes_kept: (v) => (v ? `${plural(v, 'fix', 'fixes')} kept` : null),
    fixes_rolled_back: (v) => (v ? `${plural(v, 'fix', 'fixes')} rolled back` : null),
    unchecked: (v) => (v ? `${plural(v, 'answer')} not checked` : null),
    partial: (v) => (v ? 'stopped early: its budget ran out' : null),
    cannot_act: (v) => (v ? 'stopped early: a fix couldn’t be made' : null),   // #1956
    amendments_kept: (v) => (v ? `${plural(v, 'plan change')} kept` : null),
    measured_after_amend: () => null,
    seed_unknown: () => null,                          // #1481: a question naming no dashboard seed of the build's
    perspective_shape: (v) => (v ? `${plural(v, 'perspective gap')} left for your review` : null),
    warehouse_bytes: () => null,
    warehouse_seconds: () => null,
  },
  repair: { round: () => null, claims: () => null },
};

/** A phase's counts in words, in the server's order; unknown counts are not shown. */
export function countSentence(phase: PhaseView): string {
  const words = COUNT_WORDS[phase.step] || {};
  return Object.entries(phase.counts || {})
    .map(([name, v]) => (typeof v === 'number' && words[name] ? words[name](v) : null))
    .filter((s): s is string => !!s)
    .join(' · ');
}

export function roundsSentence(phase: PhaseView): string | null {
  if (!phase.rounds) return null;
  if (phase.step === 'check') return plural(phase.rounds, 'repair round');
  if (phase.step === 'qa') return plural(phase.rounds, 'round');
  return null;
}

/** Why a build ended, in words. */
export const REASON_WORDS: Record<string, string> = {
  stopped: 'You stopped it. What it learned is kept in its draft.',
  stopped_before_start: 'Stopped before it started.',
  removed_from_queue: 'Removed from the queue before it started.',
  qa_partial: 'Built. Its answer checks stopped early, because their budget ran out or a fix couldn’t be made; the draft keeps its last checked version.',
  budget: 'It used the whole budget for this build.',
  org_month_cap: 'This workspace has used its model budget for the month. What the build learned is kept in its draft.',
  org_month_unavailable: 'The workspace’s monthly model budget couldn’t be checked, so no model call was made.',
  switch_off: 'Building with Stroma was turned off while it ran.',
  table_not_found: 'A chosen table isn’t in the warehouse any more.',
  no_tables_found: 'None of the chosen tables are in the warehouse.',
  warehouse_unavailable: 'The warehouse didn’t answer, even after several tries. What the build learned is kept in its draft.',
  one_schema_only: 'Its tables are in more than one schema; a build reads one schema for now.',
  table_name_collision: 'Two of its tables share a name in different schemas; choose one of them for this build.',
  one_catalog_only: 'Its tables are in more than one database or project; a build reads one for now.',
  table_name_unplannable: 'A chosen table’s name isn’t one a build can plan yet (lower-case letters, digits and underscores).',
  schema_name_unplannable: 'A chosen schema’s name isn’t one a build can read yet (lower-case letters, digits and underscores).',
  model_does_not_validate: 'The model it drafted didn’t pass Stroma’s checks after every try.',
  model_not_bound: 'The model it drafted kept drifting from the tested plan.',
  unattributed_check: 'A test result couldn’t be tied to the plan.',
  qa_no_questions: 'No questions could be drafted to check its answers.',
  resume_mismatch: 'Its saved progress didn’t match its draft, so it couldn’t resume.',
  journal_unwritable: 'Its progress couldn’t be saved.',
  checkpoint_failed: 'Its draft couldn’t be saved.',
  bad_spec: 'Its settings are no longer valid.',
  bad_outcome: 'It ended without a result.',
  session_error: 'It stopped on an internal error.',
  internal_error: 'It stopped on an internal error.',
  model_unavailable: 'The model provider wasn’t available.',
  model_error: 'The model provider returned an error.',
  model_refusal: 'The model declined the request.',
  model_request_refused: 'The model provider refused the request.',
  model_stopped: 'The model stopped before finishing.',
  not_configured: 'Building isn’t configured on this server.',
  no_trace_session: 'Its record couldn’t be opened.',
  trace_unwritable: 'Its record couldn’t be written.',
  plan_malformed: 'The plan the model drafted couldn’t be read.',
};

export function reasonSentence(code: string | null): string | null {
  if (!code) return null;
  return REASON_WORDS[code] ?? `Stopped (${code})`;
}

/** "12 min", "1 h 05 min", "40 s"; null for no time. */
export function duration(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
}

export const secondsBetween = (from: string | null, to: string | null): number | null => {
  if (!from || !to) return null;
  const s = (Date.parse(to) - Date.parse(from)) / 1000;
  return Number.isFinite(s) ? Math.max(s, 0) : null;
};

/** Snowflake's statement time as the page says it (#1999 S3): the build's own statements, not the warehouse's bill. */
export function secondsRun(v: number): string {
  return v < 60 ? `${n(v)} s of queries` : `${n(Math.round(v / 60))} min of queries`;
}

/** What the warehouse bills beyond the build's own statements, said once beside its time (#1999 S3). */
export const WAREHOUSE_TIME_NOTE = 'Your warehouse may bill more: a minimum each time it resumes, and idle time until it auto-suspends.';

/** Warehouse bytes as the page says them; null: not metered (Postgres). */
export function bytesRead(v: number | null): string {
  if (v === null || v === undefined) return 'not metered';
  const gb = v / 1e9;
  return gb < 0.1 ? 'under 0.1 GB' : `${gb.toLocaleString('en-US', { maximumFractionDigits: gb < 10 ? 1 : 0 })} GB`;
}

export const money = (usd: string) => {
  const v = Number(usd);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '—';
};

export function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Who started it, as a viewer may be told. */
export const byWhom = (b: AnyBuild) => b.owner_name || 'a workspace member';

export const NOT_FOUND = 'This build doesn’t exist, or it isn’t yours to see.';
export const QUEUED_WIDE = (k: number) => `Queued, ${k} in line. At most two builds run at once in this workspace; this one starts by itself.`;
export const QUEUED_MEMBER = 'At most two builds run at once in this workspace; this one starts by itself.';
export const STOPPING = 'Stopping after the current statement; what it learned is kept.';
export const FIRE_AND_FORGET = 'This runs on Stroma’s servers. Close the tab; open this link or the Builds page to check on it whenever you like.';
export const STOP_CONFIRM = 'It keeps what it learned so far; a stopped build can’t be resumed.';
export const OFF = 'Building a semantic layer with Stroma isn’t turned on in this workspace.';

export function documentsSentence(d: { read: number; restricted: number; missing: number; refused: number } | null): string | null {
  if (!d) return null;
  const parts = [
    d.read ? `${plural(d.read, 'document')} read` : null,
    d.restricted ? `${plural(d.restricted, 'document')} not read: in an organization workspace only you can read an upload, and Stroma doesn’t yet ask you to release it to your team` : null,
    d.missing ? `${plural(d.missing, 'document')} no longer there` : null,
    d.refused ? `${plural(d.refused, 'document')} the build couldn’t read` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
